import axios from 'axios';
import {
  ApiResponse,
  Role,
  Permission,
  AdminProfile,
  RolesResponse,
  PermissionsResponse,
  UserRolesResponse,
  UserPermissionsResponse,
  AdminProfilesResponse,
  ActionLogsResponse
} from '../types';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Helper to get auth token
const getAuthHeaders = () => {
  const token = useAuthStore.getState().token;
  return {
    ...(token && token !== 'null' ? { Authorization: `Bearer ${token}` } : {})
  };
};

// ============ PERMISSIONS ============

export const getAllPermissions = async (category?: string): Promise<PermissionsResponse> => {
  const response = await axios.get<ApiResponse<PermissionsResponse>>(
    `${API_BASE_URL}/api/rbac/permissions${category ? `?category=${category}` : ''}`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

export const createPermission = async (permission: Partial<Permission>): Promise<Permission> => {
  const response = await axios.post<ApiResponse<{ permission: Permission }>>(
    `${API_BASE_URL}/api/rbac/permissions`,
    permission,
    { headers: getAuthHeaders() }
  );
  return response.data.data.permission;
};

// ============ ROLES ============

export const getAllRoles = async (): Promise<RolesResponse> => {
  const response = await axios.get<ApiResponse<RolesResponse>>(
    `${API_BASE_URL}/api/rbac/roles`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

export const getRoleById = async (roleId: string): Promise<Role> => {
  const response = await axios.get<ApiResponse<{ role: Role }>>(
    `${API_BASE_URL}/api/rbac/roles/${roleId}`,
    { headers: getAuthHeaders() }
  );
  return response.data.data.role;
};

export const createRole = async (role: Partial<Role>): Promise<Role> => {
  const response = await axios.post<ApiResponse<{ role: Role }>>(
    `${API_BASE_URL}/api/rbac/roles`,
    role,
    { headers: getAuthHeaders() }
  );
  return response.data.data.role;
};

export const updateRole = async (roleId: string, role: Partial<Role>): Promise<Role> => {
  const response = await axios.put<ApiResponse<{ role: Role }>>(
    `${API_BASE_URL}/api/rbac/roles/${roleId}`,
    role,
    { headers: getAuthHeaders() }
  );
  return response.data.data.role;
};

export const deleteRole = async (roleId: string): Promise<void> => {
  await axios.delete(
    `${API_BASE_URL}/api/rbac/roles/${roleId}`,
    { headers: getAuthHeaders() }
  );
};

// ============ USER ROLES ============

export const getUserRoles = async (userId: string): Promise<UserRolesResponse> => {
  const response = await axios.get<ApiResponse<UserRolesResponse>>(
    `${API_BASE_URL}/api/rbac/users/${userId}/roles`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

export const setUserRoles = async (
  userId: string,
  roles: Role[],
  primaryRole?: string
): Promise<UserRolesResponse> => {
  const response = await axios.put<ApiResponse<UserRolesResponse>>(
    `${API_BASE_URL}/api/rbac/users/${userId}/roles`,
    { roles, primaryRole },
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

export const getUserPermissions = async (userId: string): Promise<UserPermissionsResponse> => {
  const response = await axios.get<ApiResponse<UserPermissionsResponse>>(
    `${API_BASE_URL}/api/rbac/users/${userId}/permissions`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

// ============ ADMIN PROFILES ============

export const getAllAdminProfiles = async (): Promise<AdminProfilesResponse> => {
  const response = await axios.get<ApiResponse<AdminProfilesResponse>>(
    `${API_BASE_URL}/api/rbac/admin-profiles`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

export const getAdminProfileById = async (profileId: string): Promise<AdminProfile> => {
  const response = await axios.get<ApiResponse<{ profile: AdminProfile }>>(
    `${API_BASE_URL}/api/rbac/admin-profiles/${profileId}`,
    { headers: getAuthHeaders() }
  );
  return response.data.data.profile;
};

export const updateAdminProfile = async (
  profileId: string,
  updates: Partial<AdminProfile>
): Promise<AdminProfile> => {
  const response = await axios.put<ApiResponse<{ profile: AdminProfile }>>(
    `${API_BASE_URL}/api/rbac/admin-profiles/${profileId}`,
    updates,
    { headers: getAuthHeaders() }
  );
  return response.data.data.profile;
};

// ============ ACTION LOGS ============

export const getActionLogs = async (params?: {
  page?: number;
  limit?: number;
  adminId?: string;
  action?: string;
  resource?: string;
}): Promise<ActionLogsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.adminId) queryParams.append('adminId', params.adminId);
  if (params?.action) queryParams.append('action', params.action);
  if (params?.resource) queryParams.append('resource', params.resource);

  const response = await axios.get<ApiResponse<ActionLogsResponse>>(
    `${API_BASE_URL}/api/rbac/action-logs${queryParams.toString() ? `?${queryParams}` : ''}`,
    { headers: getAuthHeaders() }
  );
  return response.data.data;
};

// ============ PERMISSION CHECKING UTILITIES ============

/**
 * Check if a user has a specific role
 */
export const userHasRole = (user: any, roleName: string): boolean => {
  if (!user) return false;

  // Check RBAC roles
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role: Role) => role.name === roleName);
  }

  // Fallback to isAdmin for backward compatibility
  if (roleName === 'admin') {
    return user.isAdmin === true;
  }

  if (roleName === 'student') {
    return !user.isAdmin;
  }

  return false;
};

/**
 * Check if a user has any of the specified roles
 */
export const userHasAnyRole = (user: any, roleNames: string[]): boolean => {
  if (!user) return false;

  // Check RBAC roles
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role: Role) => roleNames.includes(role.name));
  }

  // Fallback to isAdmin
  if (roleNames.includes('admin')) {
    return user.isAdmin === true;
  }

  return false;
};

/**
 * Check if a user has all of the specified roles
 */
export const userHasAllRoles = (user: any, roleNames: string[]): boolean => {
  if (!user) return false;

  // Check RBAC roles
  if (user.roles && Array.isArray(user.roles)) {
    const userRoleNames = user.roles.map((role: Role) => role.name);
    return roleNames.every(roleName => userRoleNames.includes(roleName));
  }

  // Fallback to isAdmin
  if (roleNames.includes('admin')) {
    return user.isAdmin === true;
  }

  return false;
};

/**
 * Check if a user is any kind of admin
 */
export const userIsAdmin = (user: any): boolean => {
  if (!user) return false;

  // Check RBAC roles
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role: Role) =>
      ['super_admin', 'admin', 'moderator', 'analyst', 'content_manager'].includes(role.name)
    );
  }

  // Fallback to isAdmin
  return user.isAdmin === true;
};

/**
 * Check if a user is a super admin
 */
export const userIsSuperAdmin = (user: any): boolean => {
  if (!user) return false;

  // Check RBAC roles
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some((role: Role) => role.name === 'super_admin');
  }

  // Super admins should have isAdmin true
  return user.isAdmin === true;
};

/**
 * Get all permissions for a user (flattened from roles)
 */
export const getUserPermissionsList = (user: any): string[] => {
  if (!user) return [];

  const permissions = new Set<string>();

  if (user.roles && Array.isArray(user.roles)) {
    user.roles.forEach((role: Role) => {
      if (role.permissions && Array.isArray(role.permissions)) {
        role.permissions.forEach((permission: Permission) => {
          permissions.add(permission.name);
        });
      }
    });
  }

  // If isAdmin but no roles, assume all permissions (backward compatibility)
  if (user.isAdmin && permissions.size === 0) {
    return ['*']; // Wildcard for all permissions
  }

  return Array.from(permissions);
};

/**
 * Check if a user has a specific permission
 */
export const userHasPermission = (user: any, permissionName: string): boolean => {
  if (!user) return false;

  const permissions = getUserPermissionsList(user);

  // Check for wildcard permission
  if (permissions.includes('*')) {
    return true;
  }

  return permissions.includes(permissionName);
};

/**
 * Get display name for a role
 */
export const getRoleDisplayName = (roleName: string): string => {
  const roleNames: Record<string, string> = {
    super_admin: '超级管理员',
    admin: '管理员',
    moderator: '内容审核员',
    analyst: '数据分析师',
    content_manager: '内容管理员',
    student: '学生'
  };

  return roleNames[roleName] || roleName;
};

/**
 * Get color for a role badge
 */
export const getRoleBadgeColor = (roleName: string): string => {
  const roleColors: Record<string, string> = {
    super_admin: '#FF0000',
    admin: '#FF6B00',
    moderator: '#FFB800',
    analyst: '#00B8FF',
    content_manager: '#00FF88',
    student: '#888888'
  };

  return roleColors[roleName] || '#888888';
};
